import { useCallback, useEffect, useRef, useState } from "react";
import { probeWebGL2 } from "./webgl2.js";
import {
  aggregatePerfReport,
  DEFAULT_POST_EFFECTS,
  PERF_PASS_CRITERIA,
  PERF_SAMPLE_SECONDS,
  resolvePerfSampleSeconds,
  type PerfReport,
} from "./perf-stats.js";

type RunState = "idle" | "running" | "done";

export interface PerfPanelProps {
  sceneReady: boolean;
  assetLoadMs: number;
  playing: boolean;
  onEnsurePlaying: () => void;
  packId: string;
}

export function PerfPanel({
  sceneReady,
  assetLoadMs,
  playing,
  onEnsurePlaying,
  packId,
}: PerfPanelProps) {
  const sampleSeconds =
    typeof window !== "undefined"
      ? resolvePerfSampleSeconds(window.location.search)
      : PERF_SAMPLE_SECONDS;
  const [runState, setRunState] = useState<RunState>("idle");
  const [report, setReport] = useState<PerfReport | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [copyNote, setCopyNote] = useState<string | null>(null);
  const framesRef = useRef<number[]>([]);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef(0);
  const lastRef = useRef(0);

  const stop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const finish = useCallback(
    (frameTimes: number[], seconds: number) => {
      const gl = probeWebGL2();
      const canvas = document.querySelector(
        '[data-testid="preview-mount"] canvas, .reel-frame canvas',
      ) as HTMLCanvasElement | null;
      const cssWidth = canvas?.clientWidth ?? window.innerWidth;
      const cssHeight = canvas?.clientHeight ?? window.innerHeight;
      const nav = navigator as Navigator & { deviceMemory?: number };
      const next = aggregatePerfReport(
        {
          frameTimesMs: frameTimes,
          sampleSeconds: seconds,
          devicePixelRatio: window.devicePixelRatio || 1,
          cssWidth,
          cssHeight,
          renderer: gl.renderer ?? "",
          vendor: gl.vendor ?? "",
          postEffects: [...DEFAULT_POST_EFFECTS],
          assetLoadMs,
          userAgent: navigator.userAgent,
          deviceMemory: nav.deviceMemory,
          hardwareConcurrency: navigator.hardwareConcurrency,
          packId,
        },
        new Date().toISOString(),
      );
      setReport(next);
      setRunState("done");
      document.documentElement.dataset.perfReport = JSON.stringify(next);
    },
    [assetLoadMs, packId],
  );

  const start = useCallback(() => {
    if (!sceneReady || runState === "running") return;
    stop();
    framesRef.current = [];
    setReport(null);
    setCopyNote(null);
    setElapsed(0);
    setRunState("running");
    onEnsurePlaying();
    startRef.current = performance.now();
    lastRef.current = startRef.current;

    const step = (now: number) => {
      const dt = now - lastRef.current;
      lastRef.current = now;
      if (dt > 0 && dt < 5000) framesRef.current.push(dt);
      const elapsedSec = (now - startRef.current) / 1000;
      setElapsed(elapsedSec);
      if (elapsedSec >= sampleSeconds) {
        finish(framesRef.current, sampleSeconds);
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, [sceneReady, runState, stop, onEnsurePlaying, finish, sampleSeconds]);

  useEffect(() => () => stop(), [stop]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("autorun") === "1" && sceneReady && runState === "idle") {
      start();
    }
  }, [sceneReady, runState, start]);

  const copyJson = async () => {
    if (!report) return;
    const text = JSON.stringify(report, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopyNote("Copied JSON report");
    } catch {
      setCopyNote("Clipboard blocked: report is on data-perf-report");
    }
  };

  return (
    <aside className="perf-panel" data-testid="perf-panel" aria-label="Performance diagnostic">
      <header>
        <h2>Perf diagnostic</h2>
        <p>
          Fixed {sampleSeconds}s playback ({PERF_SAMPLE_SECONDS}s default). Pass: avg FPS ≥{" "}
          {PERF_PASS_CRITERIA.minAvgFps}, p95 ≤ {PERF_PASS_CRITERIA.maxP95Ms} ms, dropped ≤{" "}
          {PERF_PASS_CRITERIA.maxDroppedPct}%. Thresholds never change with duration overrides.
        </p>
        <p className="meta">Pack {packId}</p>
      </header>

      <div className="perf-actions">
        <button
          type="button"
          className="primary"
          disabled={!sceneReady || runState === "running"}
          onClick={start}
          data-testid="perf-run"
        >
          {runState === "running"
            ? `Sampling… ${elapsed.toFixed(1)}s`
            : report
              ? "Restart diagnostic"
              : `Start ${sampleSeconds}s diagnostic`}
        </button>
        {!playing && runState === "running" ? (
          <span className="perf-hint">Playback paused: resume for valid samples.</span>
        ) : null}
      </div>

      {report ? (
        <>
          <dl className="perf-stats" data-testid="perf-stats" data-pass={report.pass ? "1" : "0"}>
            <div>
              <dt>Result</dt>
              <dd className={report.pass ? "pass" : "fail"} data-testid="perf-result">
                {report.pass ? "PASS" : "FAIL"}
              </dd>
            </div>
            <div>
              <dt>Avg FPS</dt>
              <dd data-testid="perf-avg-fps">{report.avgFps}</dd>
            </div>
            <div>
              <dt>p95 frame</dt>
              <dd data-testid="perf-p95">{report.p95FrameMs} ms</dd>
            </div>
            <div>
              <dt>Dropped</dt>
              <dd data-testid="perf-dropped">{report.droppedFramePct}%</dd>
            </div>
            <div>
              <dt>Asset ready</dt>
              <dd data-testid="perf-asset-ms">{report.assetLoadMs} ms</dd>
            </div>
            <div>
              <dt>Resolution / DPR</dt>
              <dd>
                {report.resolution} · dpr {report.dpr}
              </dd>
            </div>
            <div>
              <dt>GPU</dt>
              <dd>
                {report.vendor} / {report.renderer}
              </dd>
            </div>
            <div>
              <dt>UA</dt>
              <dd className="perf-ua">{report.userAgent}</dd>
            </div>
          </dl>
          <button type="button" data-testid="perf-copy-json" onClick={() => void copyJson()}>
            Copy JSON report
          </button>
          {copyNote && <p className="hint-line">{copyNote}</p>}
          <ul className="perf-notes">
            {report.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      ) : null}
    </aside>
  );
}
